import { useState, type FormEvent } from "react";
import { Plus } from "lucide-react";
import { actions, listContracts, listVendors } from "@/lib/api";
import type { Contract } from "@/lib/types";
import { displayDate } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useData, matches, reportActionError, type ViewProps, type DialogProps } from "@/lib/app-view";
import { PageHeader } from "@/components/page-header";
import { Empty, Loading, ErrorState } from "@/components/empty-state";
import { Table } from "@/components/data-table";
import { FieldError } from "@/components/field-error";

export function Contracts({ organisation, refresh, query, can, changed }: ViewProps) {
  const { data, error, loading } = useData(
    () => listContracts(organisation.id),
    [organisation.id, refresh],
  );
  const [creating, setCreating] = useState(false);
  const [addingTo, setAddingTo] = useState<Contract>();
  const [busy, setBusy] = useState("");
  if (loading) return <Loading />;
  if (error || !data) return <ErrorState message={error} />;
  const rows = data.filter((row) =>
    matches(
      query,
      row.contract_no,
      row.title,
      row.vendors?.name,
      row.currency,
      row.status,
    ),
  );
  async function activate(id: string) {
    setBusy(id);
    try {
      await actions.activateContract(organisation.id, id);
      changed();
    } catch (reason) {
      reportActionError(reason);
    } finally {
      setBusy("");
    }
  }
  return (
    <>
      <PageHeader
        eyebrow="CONTRACTED PROCUREMENT"
        title="Contracts & rates"
        description="Buy recurring requirements against agreed commercial terms in any currency."
      >
        {can("procure") && (
          <Button onClick={() => setCreating(true)}>
            <Plus className="size-4" />
            New contract
          </Button>
        )}
      </PageHeader>
      {rows.length ? (
        <Table
          heads={[
            "Contract",
            "Supplier",
            "Term",
            "Currency",
            "Rate items",
            "Status",
            "Action",
          ]}
        >
          {rows.map((row) => (
            <tr key={row.id}>
              <td>
                <strong>{row.contract_no}</strong>
                <small className="block text-slate-500">{row.title}</small>
              </td>
              <td>{row.vendors?.name}</td>
              <td>
                {displayDate(row.starts_on)} — {displayDate(row.ends_on)}
              </td>
              <td>
                {row.currency}
                <small className="block text-slate-500">
                  Base rate {row.exchange_rate_to_base}
                </small>
              </td>
              <td>{row.contract_items?.length || 0}</td>
              <td>
                <Badge value={row.status} />
              </td>
              <td>
                <div className="flex gap-2">
                  {can("procure") && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setAddingTo(row)}
                    >
                      Add rate
                    </Button>
                  )}
                  {can("procure") && row.status === "draft" && (
                    <Button
                      size="sm"
                      disabled={busy === row.id}
                      onClick={() => activate(row.id)}
                    >
                      Activate
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </Table>
      ) : (
        <Empty
          title="No contracted rates yet"
          description="Create a supplier agreement, add rate items and activate it for reordering."
          action={
            can("procure") && (
              <Button onClick={() => setCreating(true)}>Create contract</Button>
            )
          }
        />
      )}
      <ContractDialog
        open={creating}
        onOpenChange={setCreating}
        organisationId={organisation.id}
        onDone={changed}
      />
      <ContractItemDialog
        contract={addingTo}
        onOpenChange={(open) => !open && setAddingTo(undefined)}
        organisationId={organisation.id}
        onDone={changed}
      />
    </>
  );
}

function ContractDialog({
  open,
  onOpenChange,
  organisationId,
  onDone,
}: DialogProps) {
  const { data: vendors } = useData(
    () => listVendors(organisationId),
    [organisationId, open],
  );
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    try {
      await actions.createContract(organisationId, {
        p_vendor_id: form.get("vendor"),
        p_contract_no: form.get("contractNo"),
        p_title: form.get("title"),
        p_currency: String(form.get("currency")).toUpperCase(),
        p_exchange_rate_to_base: Number(form.get("rate")),
        p_starts_on: form.get("starts"),
        p_ends_on: form.get("ends"),
        p_minimum_order_value: Number(form.get("minimum")),
        p_payment_terms_days: Number(form.get("terms")),
        p_notes: form.get("notes"),
      });
      onOpenChange(false);
      onDone();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader
          eyebrow="CONTRACTED PROCUREMENT"
          title="New supplier contract"
        />
        <form onSubmit={submit} className="grid gap-4 p-6">
          <div className="grid gap-4 md:grid-cols-2">
            <Label>
              Supplier
              <Select name="vendor" required>
                {vendors?.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name}
                  </option>
                ))}
              </Select>
            </Label>
            <Label>
              Contract number
              <Input name="contractNo" required />
            </Label>
            <Label>
              Contract title
              <Input name="title" required />
            </Label>
            <Label>
              Currency
              <Input
                name="currency"
                defaultValue="INR"
                pattern="[A-Za-z]{3}"
                maxLength={3}
                required
              />
            </Label>
            <Label>
              Exchange rate to workspace base
              <Input
                name="rate"
                type="number"
                min="0.00000001"
                step="0.00000001"
                defaultValue="1"
                required
              />
            </Label>
            <Label>
              Minimum order value
              <Input
                name="minimum"
                type="number"
                min="0"
                step="0.01"
                defaultValue="0"
                required
              />
            </Label>
            <Label>
              Starts on
              <Input name="starts" type="date" required />
            </Label>
            <Label>
              Ends on
              <Input name="ends" type="date" required />
            </Label>
            <Label>
              Payment terms (days)
              <Input
                name="terms"
                type="number"
                min="1"
                defaultValue="30"
                required
              />
            </Label>
          </div>
          <Label>
            Commercial notes
            <Textarea name="notes" />
          </Label>
          <FieldError>{error}</FieldError>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button disabled={busy} type="submit">
              Create contract
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ContractItemDialog({
  contract,
  onOpenChange,
  organisationId,
  onDone,
}: {
  contract?: Contract;
  onOpenChange: (open: boolean) => void;
  organisationId: string;
  onDone: () => void;
}) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!contract) return;
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    try {
      await actions.addContractItem(organisationId, {
        p_contract_id: contract.id,
        p_sku: form.get("sku"),
        p_description: form.get("description"),
        p_specification: form.get("specification"),
        p_unit: form.get("unit"),
        p_contracted_price: Number(form.get("price")),
        p_minimum_quantity: Number(form.get("minimum")),
        p_lead_days: Number(form.get("lead")),
      });
      onOpenChange(false);
      onDone();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open={Boolean(contract)} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader
          eyebrow="CONTRACT RATE"
          title={`Add rate to ${contract?.contract_no || "contract"}`}
        />
        <form onSubmit={submit} className="grid gap-4 p-6">
          <div className="grid gap-4 md:grid-cols-2">
            <Label>
              SKU
              <Input name="sku" />
            </Label>
            <Label>
              Description
              <Input name="description" required />
            </Label>
            <Label>
              Unit
              <Input name="unit" placeholder="Nos / Box / Kit" required />
            </Label>
            <Label>
              Contracted price ({contract?.currency})
              <Input name="price" type="number" min="0" step="0.01" required />
            </Label>
            <Label>
              Minimum quantity
              <Input
                name="minimum"
                type="number"
                min="0.01"
                step="0.01"
                defaultValue="1"
                required
              />
            </Label>
            <Label>
              Lead days
              <Input
                name="lead"
                type="number"
                min="0"
                defaultValue="0"
                required
              />
            </Label>
          </div>
          <Label>
            Specification
            <Textarea name="specification" required />
          </Label>
          <FieldError>{error}</FieldError>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button disabled={busy} type="submit">
              Add contracted rate
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
